import { useCallback, useContext, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { ModalContext } from 'components/ModalProvider/ModalProvider';
import { actions } from 'features/users/redux';
import { useAgentPasswordComplexity } from 'shared/hooks/useAgentPasswordComplexity';

const initialForm = {
  oldPassword: '',
  newPassword: '',
  confirmNewPassword: '',
};

const useRequiredChangePassword = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const { closeModal } = useContext(ModalContext);

  const { id } = useSelector(state => state.auth, shallowEqual);
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const actionProcessing = useSelector(state => state.users.actionProcessing);

  const [form, setForm] = useState(initialForm);

  const complexity = useAgentPasswordComplexity({
    password: form.newPassword,
    locale,
  });

  const isPasswordsMatched = form.newPassword === form.confirmNewPassword;

  const disabled =
    actionProcessing ||
    !form.oldPassword ||
    !complexity.isValid ||
    !isPasswordsMatched;

  const changeValue = useCallback(e => {
    const { name, value } = e.currentTarget;
    setForm(prevForm => ({ ...prevForm, [name]: value.trim() }));
  }, []);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      if (disabled) return;

      dispatch(
        actions.changePassword({
          data: {
            old_password: form.oldPassword,
            password: form.newPassword,
          },
          id,
          callback: () => {
            closeModal();
            onSuccess && onSuccess();
          },
        }),
      );
    },
    [closeModal, disabled, dispatch, form.newPassword, form.oldPassword, id, onSuccess],
  );

  return {
    form,
    locale,
    complexity,
    disabled,
    isPasswordsMatched,
    closeModal,
    changeValue,
    onSubmit,
  };
};

export default useRequiredChangePassword;
